import type { AuditOutcomeValue } from "@amtana/audit-contract";
import type { Catalogs, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";

// Pure copy for the audit log screen (S4.08): no React, no I/O.

export type AuditCopyKey = keyof typeof EN;
export type AuditCatalog = typeof EN;

const EN = {
  screenTitle: "Audit log · {product}",
  navAuditLog: "Audit log",
  breadcrumbHome: "Home",
  breadcrumbSettings: "Settings",
  auditLogTitle: "Audit log",
  auditLogDescription:
    "Every permissioned write made in this organization, who did it and whether it went through.",
  resource: "Resource",
  resourcePlaceholder: "members",
  outcome: "Outcome",
  anyOutcome: "Any outcome",
  outcomeAllowed: "Allowed",
  outcomeRefused: "Refused",
  actorLabel: "Actor (user id)",
  actorPlaceholder: "u1",
  loadMore: "Load more",
  emptyTitle: "Nothing in the audit log yet",
  columnWhen: "When",
  columnWho: "Who",
  columnWhat: "What",
  columnTarget: "Target",
  columnOutcome: "Outcome",
  columnReason: "Reason",
  staff: "Staff",
  actingAs: "acting as {name}",
  agent: "Agent",
  onBehalfOf: "on behalf of {name}",
  apiKey: "API key",
  createdBy: "(created by {name})",
} as const;

const ES: Translation<AuditCatalog> = {
  screenTitle: "Registro de auditoría · {product}",
  navAuditLog: "Registro de auditoría",
  breadcrumbHome: "Inicio",
  breadcrumbSettings: "Ajustes",
  auditLogTitle: "Registro de auditoría",
  auditLogDescription: "Cada cambio con permisos hecho en esta organización, quién lo hizo y si se permitió.",
  resource: "Recurso",
  resourcePlaceholder: "members",
  outcome: "Resultado",
  anyOutcome: "Cualquier resultado",
  outcomeAllowed: "Permitido",
  outcomeRefused: "Rechazado",
  actorLabel: "Actor (id de usuario)",
  actorPlaceholder: "u1",
  loadMore: "Cargar más",
  emptyTitle: "Aún no hay nada en el registro de auditoría",
  columnWhen: "Cuándo",
  columnWho: "Quién",
  columnWhat: "Qué",
  columnTarget: "Destino",
  columnOutcome: "Resultado",
  columnReason: "Motivo",
  staff: "Equipo de {product}",
  actingAs: "en nombre de {name}",
  agent: "Agente",
  onBehalfOf: "por cuenta de {name}",
  apiKey: "Clave de API",
  createdBy: "(creada por {name})",
};

export const AUDIT_CATALOGS: Catalogs<AuditCatalog> = { en: EN, es: ES };

const OUTCOME_KEY: Readonly<Record<AuditOutcomeValue, PlainKey<AuditCatalog>>> = {
  allowed: "outcomeAllowed",
  refused: "outcomeRefused",
};

/** An outcome's name in the active language. */
export function auditOutcomeLabel(outcome: AuditOutcomeValue, t: Translate<AuditCatalog>): string {
  return t(OUTCOME_KEY[outcome]);
}

/** The outcome filter: "any" first (an empty value), then each outcome. */
export function auditOutcomeOptions(
  t: Translate<AuditCatalog>,
): ReadonlyArray<{ readonly value: string; readonly label: string }> {
  return [
    { value: "", label: t("anyOutcome") },
    { value: "allowed", label: auditOutcomeLabel("allowed", t) },
    { value: "refused", label: auditOutcomeLabel("refused", t) },
  ];
}
